/*
 * Session and auth hooks. The session query is the source of truth for "signed in": guards read it, the
 * shell reads the organisation summary from it. Sign-in flows store the token, then refetch the session.
 */
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/api/client'
import { authToken } from '@/api/token'
import type {
  AuthToken,
  DemoPersona,
  Session,
  SignInBody,
  SignInResult,
  SignUpBody,
  SignUpResult,
  TotpBody,
} from '@/api/types'

export const authKeys = {
  session: ['auth', 'session'] as const,
  inbox: (to: string) => ['mock', 'inbox', to] as const,
}

export const sessionQuery = () =>
  queryOptions({
    queryKey: authKeys.session,
    queryFn: ({ signal }) =>
      authToken.get()
        ? api.get<Session>('/auth/session', { signal, anonymous: true })
        : Promise.resolve(null),
    staleTime: 60_000,
    retry: false,
  })

/** The current session, or null when signed out. */
export function useSession() {
  return useQuery(sessionQuery())
}

/** The session, asserted present: use inside the authenticated shell only. */
export function useAuth(): Session {
  const { data } = useSession()
  if (!data) throw new Error('useAuth() used outside an authenticated route')
  return data
}

/** Stores a token and loads the session for it. */
function useAcceptToken() {
  const qc = useQueryClient()
  return async (t: AuthToken) => {
    authToken.set(t.token)
    qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' })
    await qc.fetchQuery({ ...sessionQuery(), staleTime: 0 })
  }
}

export function useSignUp() {
  return useMutation({
    mutationFn: (body: SignUpBody) => api.post<SignUpResult>('/auth/signup', body, { anonymous: true }),
    meta: { silent: true },
  })
}

export function useResendVerification() {
  return useMutation({
    mutationFn: (email: string) => api.post('/auth/resend-verification', { email }, { anonymous: true }),
  })
}

export function useVerifyEmail() {
  const accept = useAcceptToken()
  return useMutation({
    mutationFn: async (token: string) => {
      const t = await api.post<AuthToken>('/auth/verify-email', { token }, { anonymous: true })
      await accept(t)
      return t
    },
    meta: { silent: true },
  })
}

export function useSignIn() {
  const accept = useAcceptToken()
  return useMutation({
    mutationFn: async (body: SignInBody) => {
      const r = await api.post<SignInResult>('/auth/login', body, { anonymous: true })
      if (r.status === 'signed_in') await accept(r)
      return r
    },
    meta: { silent: true },
  })
}

export function useVerifyTotp() {
  const accept = useAcceptToken()
  return useMutation({
    mutationFn: async (body: TotpBody) => {
      const t = await api.post<AuthToken>('/auth/login/totp', body, { anonymous: true })
      await accept(t)
      return t
    },
    meta: { silent: true },
  })
}

export function useDemoSignIn() {
  const accept = useAcceptToken()
  return useMutation({
    mutationFn: async (persona: DemoPersona) => {
      const t = await api.post<AuthToken>('/auth/demo', { persona }, { anonymous: true })
      await accept(t)
      return t
    },
  })
}

export function useForgotPassword() {
  return useMutation({
    mutationFn: (email: string) => api.post('/auth/forgot-password', { email }, { anonymous: true }),
    meta: { silent: true },
  })
}

export function useResetPassword() {
  return useMutation({
    mutationFn: (body: { token: string; password: string }) =>
      api.post('/auth/reset-password', body, { anonymous: true }),
    meta: { silent: true },
  })
}

export function useSignOut() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      try {
        await api.post('/auth/logout', {}, { anonymous: true })
      } finally {
        authToken.clear()
        qc.clear()
        qc.setQueryData(authKeys.session, null)
      }
    },
    meta: { silent: true },
  })
}

export function useSwitchOrganization() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (organizationId: string) => api.post('/auth/switch-organization', { organizationId }),
    onSuccess: async () => {
      qc.removeQueries({ predicate: (q) => q.queryKey[0] !== 'auth' })
      await qc.invalidateQueries({ queryKey: authKeys.session })
    },
  })
}

/** Mock only: the emails the mock "sent" to an address (verification, reset). */
export function useMockInbox(to: string | null, enabled: boolean) {
  return useQuery({
    queryKey: authKeys.inbox(to ?? ''),
    queryFn: ({ signal }) =>
      api.get<{ id: string; kind: string; link: string; createdAt: string }[]>('/_mock/inbox', {
        query: { to },
        signal,
        anonymous: true,
      }),
    enabled: enabled && !!to,
    refetchInterval: 4000,
  })
}
